/* import __COLOCATED_TEMPLATE__ from './segmentation-settings-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type Store from '@ember-data/store';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type BrandService from 'embercom/services/brand-service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import type SuggestionSegmentationRule from 'embercom/models/knowledge-base/suggestion-segmentation-rule';
import { post } from 'embercom/lib/ajax';

interface Args {
  isOpen: boolean;
  onClose: () => void;
}

export default class SegmentationSettingsDrawer extends Component<Args> {
  @service declare store: Store;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare brandService: BrandService;
  @service declare permissionsService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked suggestionSegmentationRules: Array<SuggestionSegmentationRule> = [];
  @tracked selectedSegmentIds: Set<string> = new Set();
  @tracked showSaveModal = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadSuggestionSegmentationRulesTask).perform();
    if (!this.aiContentSegmentsService.isLoading) {
      this.aiContentSegmentsService.loadSegments();
    }
    if (this.brandService.isAppOnRightMultibrandPlan) {
      this.brandService.loadBrands();
    }
  }

  get aiContentSegments() {
    return this.aiContentSegmentsService.segments;
  }

  get isLoading() {
    return (
      taskFor(this.loadSuggestionSegmentationRulesTask).isRunning ||
      this.aiContentSegmentsService.isLoading
    );
  }

  get showBrandWarning() {
    let currentlySelectedAudiences = this.aiContentSegments.filter((segment) =>
      this.selectedSegmentIds.has(segment.id),
    );
    let currentlySelectedHaveBrandAttribute = currentlySelectedAudiences.every(
      (audience) => audience.hasBrandAttribute,
    );

    return (
      this.aiContentSegmentsService.hasLoadedSegments &&
      this.brandService.isMultibrandWorkspace &&
      this.selectedSegmentIds.size > 0 &&
      !currentlySelectedHaveBrandAttribute
    );
  }

  get isUpdatingSegmentationRules() {
    return taskFor(this.updateSegmentationRulesTask).isRunning;
  }

  get hasUnsavedChanges() {
    let originalSegmentIds = new Set(
      this.suggestionSegmentationRules.map((rule) => rule.aiContentSegmentId),
    );

    if (this.selectedSegmentIds.size !== originalSegmentIds.size) {
      return true;
    }

    for (let segmentId of this.selectedSegmentIds) {
      if (!originalSegmentIds.has(segmentId)) {
        return true;
      }
    }

    return false;
  }

  @action
  async cancel() {
    this.selectedSegmentIds = new Set(
      this.suggestionSegmentationRules.map((rule) => rule.aiContentSegmentId),
    );
    this.args.onClose();
  }

  @action
  clearSegments() {
    this.selectedSegmentIds = new Set();
  }

  @action
  selectSegment(segment: AiContentSegment) {
    if (this.selectedSegmentIds.has(segment.id)) {
      this.selectedSegmentIds.delete(segment.id);
    } else {
      this.selectedSegmentIds.add(segment.id);
    }
    // Trigger reactivity
    this.selectedSegmentIds = new Set(this.selectedSegmentIds);
  }

  @action
  async save() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }

    try {
      await taskFor(this.updateSegmentationRulesTask).perform();
      await taskFor(this.loadSuggestionSegmentationRulesTask).perform();
      this.intercomEventService.trackAnalyticsEvent({
        action: 'saved',
        object: 'segmentation_settings',
      });
      if (this.appService.app.canUseSuggestionAudienceTargetingRegenerate) {
        this.notificationsService.notifyConfirmation(
          this.intl.t('ai-agent.suggestions.settings.save-settings.success'),
        );
      } else {
        this.notificationsService.notifyConfirmation(
          this.intl.t('ai-agent.suggestions.settings.generation-finished.success'),
        );
      }
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.suggestions.settings.save-settings.error'),
      );
    }
    this.showSaveModal = false;
    this.args.onClose();
  }

  @dropTask
  *loadSuggestionSegmentationRulesTask(): TaskGenerator<void> {
    let results = yield this.store.findAll('knowledge-base/suggestion-segmentation-rule');
    this.suggestionSegmentationRules = results.toArray();
    this.selectedSegmentIds = new Set(
      this.suggestionSegmentationRules.map((rule) => rule.aiContentSegmentId),
    );
  }

  @dropTask
  *updateSegmentationRulesTask() {
    yield post('/ember/knowledge_base/suggestion_segmentation_rules/bulk_update', {
      app_id: this.appService.app.id,
      segment_ids: Array.from(this.selectedSegmentIds),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Suggestions::SegmentationSettingsDrawer': typeof SegmentationSettingsDrawer;
  }
}
